// postService.js: REST calls for the feed/posts domain — report categories,
// post replies, and the unread-posts pagination/mark-as-read flow used by
// the feed store (see store/feed/operations.js) to track which posts a
// user has already seen.

/**
 * Fetch all report categories (reasons) from the backend.
 * Used to populate the tag list in the "Report this post" dialog.
 * @returns {Promise<Array>} - Array of { id, name } category objects
 */
export async function fetchReportCategories() {
  try {
    const response = await fetch('/api/report-categories');
    if (!response.ok) return [];
    const data = await response.json();
    // Handle both array responses and wrapped { results, data } shapes
    return Array.isArray(data) ? data : (data.results ?? data.data ?? []);
  } catch (err) {
    console.error('Error fetching report categories:', err);
    return [];
  }
}

/**
 * Fetch the replies for a single post.
 * @param {string} id - Post ID.
 * @returns {Promise<Array|undefined>} - Array of reply objects, or undefined on error/failed response.
 */
export async function fetchReplies(id) {
  try {
    let response = await fetch(`/api/posts/${id}/replies`);
    let replies = await response.json();
    if (response.ok) {
      return replies;
    }
    if (replies.statusCode === 401 || replies.statusCode === 403) {
      console.error('Replies not found');
    }
  } catch (err) {
    console.error(err);
  }
}

/**
 * Get user unread posts
 * @param {Object} params - Request parameters
 * @param {string} params.authorUserId - User ID
 * @param {number} [params.offset=0] - Pagination offset
 * @param {number} [params.limit=15] - Number of posts per page
 * @returns {Promise<Object>} - Response object with results
 */
/**
 * @param {{ authorUserId: string; offset?: number; limit?: number; after?: string }} params
 */
export async function fetchUserUnreadPosts(params) {
  try {
    const sp = new URLSearchParams({
      authorUserId: String(params.authorUserId ?? ''),
      limit: String(params.limit || 15),
    });
  if (params && 'after' in params && params.after) sp.append('after', String(params.after));
    const queryParams = sp.toString();

    const response = await fetch(`/api/posts/unread?${queryParams}`, {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      console.error('Error fetching unread posts:', response.statusText);
      return { results: [] };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return { results: [] };
  }
}

/**
 * Mark posts as read by user (bulk or paginated)
 * @param {Object} params - Request parameters
 * @param {string} params.authorUserId - User ID (required)
 * @param {number} [params.limit] - Optional limit
 * @param {string} [params.after] - Optional pagination cursor
 * @param {boolean} [params.reversed] - Optional reversed flag
 * @returns {Promise<Response>} - API response
 */
export async function markPostsAsRead({ authorUserId, limit, after, reversed }) {
  try {
    const body = { authorUserId };
    if (limit !== undefined) body.limit = Number(limit);
    if (after !== undefined) body.after = String(after);
    if (reversed !== undefined) body.reversed = Boolean(reversed);

    const response = await fetch('/api/posts/mark-as-read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (response.status === 204) {
      return response;
    }
    return response;
  } catch (error) {
    console.error('Error marking posts as read:', error);
    return null;
  }
}

/**
 * Mark a single post as read for the current user
 * @param {string} postId - ID of the post to mark as read
 * @returns {Promise<Response>} - API response
 */
export async function markSinglePostAsRead(postId) {
  try {
    const response = await fetch(`/api/posts/${postId}/mark-as-read`, {
      method: 'POST',
    });
    return response;
  } catch (error) {
    console.error('Error marking single post as read:', error);
    return null;
  }
}
